import { UseQueryResult } from '@tanstack/react-query';
import { useSession } from '../SessionProvider';
import { useTrackedQuery as useQuery } from '@ais-ui/performance-tracking';
import { STALE_TIMES, REFETCH_INTERVALS } from '@ais-ui/custom-hooks';

import {
  checkForNewEvents,
  EventsResponse,
  getCurrentEvents,
  getEventsForPeriod,
} from './getEvents';
import { convertFieldsToDates } from '@ais-ui/utils';
import { useRefetchOnSocketChange } from '../useRefetchOnSocketChange';

// Every query below keys on the stable `sessionId`, never `olSession.socketId`
// (minted fresh per transport, so it leaves a cache entry per reconnect).
// `useRefetchOnSocketChange` still refetches against the reconnected socket,
// which is what keying on socketId used to trigger implicitly.
//
// `enabled` lets callers gate the olserver events queries on the user's events
// permission. Without it, users who can't see events still poll olserver, and
// for them `getHistoricalEvents` can fail forever with "user not found"
// (CLASSIC-10406 / CLASSIC-10275).

export const useGetEvents = (
  enabled = true,
): UseQueryResult<EventsResponse> => {
  const { olSession, sessionId } = useSession();
  const isEnabled = enabled && !!sessionId;

  const query = useQuery<EventsResponse, Error>(
    {
      queryKey: ['getCurrentEvents', sessionId],
      queryFn: () => {
        return getCurrentEvents(olSession);
      },
      staleTime: STALE_TIMES.REALTIME,
      enabled: isEnabled,
    },
    'events:getCurrent',
  );

  useRefetchOnSocketChange(olSession?.socketId, isEnabled, query.refetch);

  return query;
};

export const useGetEventsForPeriod = (
  startDateTime: Date | undefined,
  endDateTime: Date | undefined,
  enabled = true,
): UseQueryResult<EventsResponse> => {
  const { olSession, sessionId } = useSession();

  // Convert dates to strings for stable query keys, guarding against invalid dates.
  // Round to nearest 30s to reduce query key churn in trailing mode
  // (where dates change every 5s). refetchInterval handles intermediate refreshes.
  const roundTo30s = (date: Date) =>
    new Date(Math.floor(date.getTime() / 30000) * 30000).toISOString();

  const isValidStart =
    startDateTime instanceof Date && !isNaN(startDateTime.getTime());
  const isValidEnd =
    endDateTime instanceof Date && !isNaN(endDateTime.getTime());
  const startKey = isValidStart ? roundTo30s(startDateTime) : undefined;
  const endKey = isValidEnd ? roundTo30s(endDateTime) : undefined;

  const isEnabled = enabled && isValidStart && isValidEnd && !!sessionId;

  const query = useQuery<EventsResponse, Error>(
    {
      queryKey: ['getPeriodEvents', sessionId, startKey, endKey],
      queryFn: async () => {
        const response = await getEventsForPeriod(
          olSession,
          startDateTime as Date,
          endDateTime as Date,
        );

        if (response) {
          convertFieldsToDates(response, [
            'endDateTime',
            'endTime',
            'startDateTime',
            'startTime',
            'events.activated',
            'events.act_ts',
            'events.deactivated',
            'events.deact_ts',
            'events.ack_state_modified_ts',
          ]);
        }

        return response;
      },
      enabled: isEnabled,
      placeholderData: (previousData) => previousData,
      refetchInterval: REFETCH_INTERVALS.STANDARD,
    },
    'events:getForPeriod',
  );

  useRefetchOnSocketChange(olSession?.socketId, isEnabled, query.refetch);

  return query;
};

export const useCheckForNewEvents = () => {
  const { olSession, sessionId } = useSession();

  const query = useQuery(
    {
      queryKey: ['checkForNewEvents', sessionId],
      queryFn: () => checkForNewEvents(olSession),
      staleTime: STALE_TIMES.REALTIME,
      refetchInterval: REFETCH_INTERVALS.FAST * 3, // 15 seconds
      enabled: !!sessionId,
    },
    'events:checkNew',
  );

  useRefetchOnSocketChange(olSession?.socketId, !!sessionId, query.refetch);

  return query;
};
